'use client';

import { useState } from 'react';

import Icon from '@/components/ui/AppIcon';
import { useRouter } from 'next/navigation';

interface AdminSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  onLogout: () => void;
}

export default function AdminSidebar({ activeSection, onSectionChange, onLogout }: AdminSidebarProps) {
  const router = useRouter();
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'ChartBarIcon',
      path: '/admin',
    },
    {
      id: 'admin-dashboard',
      label: 'Admin Dashboard',
      icon: 'BriefcaseIcon',
      path: '/admin/dashboard',
    },
    {
      id: 'gestao-ativos',
      label: 'Gestão de Ativos',
      icon: 'FolderIcon',
      subItems: [
        { id: 'blog', label: 'Gestão de Blog', path: '/admin/blog' },
        { id: 'testimonials', label: 'Depoimentos', path: '/admin/testimonials' },
      ],
    },
    {
      id: 'ecossistema-usuarios',
      label: 'Ecossistema de Usuários',
      icon: 'UsersIcon',
      subItems: [
        { id: 'users', label: 'Lista de Usuários', path: '/admin/users' },
        { id: 'approve-access', label: 'Aprovar Acessos', path: '/admin/approve-access' },
      ],
    },
    {
      id: 'configuracoes',
      label: 'Configurações',
      icon: 'CogIcon',
      subItems: [
        { id: 'site-settings', label: 'Dados do Site', path: '/admin/site-settings' },
        { id: 'profile', label: 'Meus Dados', path: '/admin/profile' },
      ],
    },
  ];

  const handleMenuClick = (item: any) => {
    if (item.subItems) {
      setExpandedMenu(expandedMenu === item.id ? null : item.id);
    } else {
      onSectionChange(item.id);
      if (item.path) {
        router.push(item.path);
      }
    }
  };

  const handleSubItemClick = (subItem: any) => {
    onSectionChange(subItem.id);
    if (subItem.path) {
      router.push(subItem.path);
    }
  };

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-obsidian-bg border-r border-obsidian-border flex flex-col">
      {/* Logo Section */}
      <div className="p-6 border-b border-obsidian-border">
        <h1 className="text-2xl font-black text-white tracking-tight">
          TELE<span className="text-obsidian-accent">MARCAS</span>
        </h1>
        <p className="text-xs text-gray-400 mt-1">Admin Panel</p>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto py-6 px-3 custom-scrollbar">
        {menuItems.map((item) => (
          <div key={item.id} className="mb-2">
            <button
              onClick={() => handleMenuClick(item)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-300 group ${
                activeSection === item.id
                  ? 'bg-obsidian-surface border border-obsidian-accent/30 text-white' :'text-gray-400 hover:bg-obsidian-surface hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon
                  name={item.icon}
                  className={`w-5 h-5 transition-colors ${
                    activeSection === item.id ? 'text-obsidian-accent' : 'group-hover:text-obsidian-cyan'
                  }`}
                />
                <span className="text-sm font-semibold">{item.label}</span>
              </div>
              {item.subItems && (
                <Icon
                  name="ChevronDownIcon"
                  className={`w-4 h-4 transition-transform ${
                    expandedMenu === item.id ? 'rotate-180' : ''
                  }`}
                />
              )}
            </button>

            {/* Sub-menu */}
            {item.subItems && expandedMenu === item.id && (
              <div className="mt-2 ml-4 space-y-1 animate-slide-in">
                {item.subItems.map((subItem) => (
                  <button
                    key={subItem.id}
                    onClick={() => handleSubItemClick(subItem)}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-all duration-300 ${
                      activeSection === subItem.id
                        ? 'bg-obsidian-accent/10 text-obsidian-accent border-l-2 border-obsidian-accent' :'text-gray-400 hover:bg-obsidian-surface hover:text-white hover:border-l-2 hover:border-obsidian-cyan'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                    {subItem.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Logout Button */}
      <div className="p-4 border-t border-obsidian-border">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-obsidian-surface border border-obsidian-border text-gray-400 hover:border-obsidian-accent hover:text-white transition-all duration-300 group"
        >
          <Icon name="ArrowRightOnRectangleIcon" className="w-5 h-5 group-hover:text-obsidian-accent" />
          <span className="text-sm font-semibold">Ejetar / Logout</span>
        </button>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #ff0000;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cc0000;
        }
      `}</style>
    </aside>
  );
}